import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { useNavigate } from "react-router-dom";

const TokenVerificationPage = () => {
  const [userId, setUserId] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isSekaimura, setIsSekaimura] = useState(false); // ★ 追加
  const [token, setToken] = useState("");
  const [sekaiMuraToken, setSekaiMuraToken] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUserId(currentUser.uid);
      const userRef = doc(db, "users", currentUser.uid);
      const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setIsVerified(!!userData.isVerified);
          setIsSekaimura(!!userData.isSekaimura); // ★ 追加
        }
      });
      return () => unsubscribe();
    } else {
      setMessage("ログインしてください。");
    }
  }, []);

  useEffect(() => {
    if (isVerified) {
      setRedirecting(true);
      const t = setTimeout(() => {
        navigate("/home");
      }, 10000);
      return () => clearTimeout(t);
    }
  }, [isVerified, navigate]);

  const handleVerify = async () => {
    if (!token) {
      setMessage("トークンを入力してください。");
      return;
    }
    if (!userId) {
      setMessage("ユーザー情報が見つかりません。ログインしてください。");
      return;
    }
    setLoading(true);
    setMessage("");

    try {
      const tokenQuery = query(
        collection(db, "token2generation"),
        where("token", "==", token)
      );
      const querySnapshot = await getDocs(tokenQuery);

      if (querySnapshot.empty) {
        setMessage("無効なトークンです。");
        setLoading(false);
        return;
      }

      const tokenData = querySnapshot.docs[0]?.data();
      if (!tokenData) {
        setMessage("トークン情報が見つかりません。");
        setLoading(false);
        return;
      }

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        isVerified: true,
        secondPhasePrice: tokenData.price,
        usedToken: token,
      });

      setMessage("認証が完了しました。");
      setRedirecting(true);
      setTimeout(() => {
        navigate("/home");
      }, 10000);
    } catch (error) {
      console.error("Error verifying token:", error.message);
      setMessage("認証中にエラーが発生しました。");
    } finally {
      setLoading(false);
    }
  };

  const handleSekaiMuraVerify = async () => {
    if (!sekaiMuraToken) {
      setMessage("セカイムラのトークンを入力してください。");
      return;
    }
    if (!userId) {
      setMessage("ユーザー情報が見つかりません。ログインしてください。");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const sekaiMuraQuery = query(
        collection(db, "sekaimuraUserToken"),
        where("token", "==", sekaiMuraToken)
      );
      const querySnapshot = await getDocs(sekaiMuraQuery);

      if (querySnapshot.empty) {
        setMessage("無効なセカイムラトークンです。");
        setLoading(false);
        return;
      }

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        isSekaimura: true,
        isVerified: true,
        usedSekaiMuraToken: sekaiMuraToken,
      });

      setMessage("セカイムラ認証が完了しました。");
      setRedirecting(true);
      setTimeout(() => {
        navigate("/home");
      }, 10000);
    } catch (error) {
      console.error("Error verifying SekaiMura token:", error.message);
      setMessage("セカイムラ認証中にエラーが発生しました。");
    } finally {
      setLoading(false);
    }
  };

  // ★ 紹介のみ（ボタン無し）
  const FeaturesIntro = () => (
    <Box sx={{ mt: 6 }}>
      <Typography variant="h6" gutterBottom>
        開放機能（紹介）
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {isSekaimura
          ? "以下の機能がご利用いただけます。"
          : "セカイムラ認証後に以下の機能が利用できます。"}
      </Typography>
      <List dense>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ChatBubbleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="一部チャット"
            secondary={
              isSekaimura
                ? "セカイムラメンバー限定のチャットに参加できます。"
                : "認証後に参加可能になります。"
            }
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        トークン認証
      </Typography>

      {isSekaimura && (
        <Alert severity="success" sx={{ mt: 2 }}>
          あなたはセカイムラメンバー認証がなされています。
        </Alert>
      )}

      {isVerified ? (
        <>
          <Typography
            variant="body1"
            color="success.main"
            align="center"
            sx={{ mt: 2 }}
          >
            認証済です。
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mt: 2 }}
          >
            10秒後にHome画面へ遷移します。
          </Typography>
        </>
      ) : (
        <>
          {!redirecting && !isSekaimura && (
            <>
              <Typography variant="body1" align="center" gutterBottom sx={{ mt: 4 }}>
                セカイムラメンバーがマスターマインドにアクセスする認証ページです。
              </Typography>

              <Typography variant="body1" align="center" gutterBottom sx={{ mt: 4 }}>
                セカイムラのトークンを入力してください。
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <TextField
                  label="セカイムラのトークン"
                  variant="outlined"
                  fullWidth
                  value={sekaiMuraToken}
                  onChange={(e) => setSekaiMuraToken(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={handleSekaiMuraVerify}
                  disabled={loading}
                >
                  {loading ? "認証中..." : "セカイムラ認証する"}
                </Button>
              </Box>
            </>
          )}

          {message && (
            <Typography
              variant="body1"
              color={message.includes("完了") ? "success.main" : "error"}
              align="center"
              sx={{ mt: 2 }}
            >
              {message}
            </Typography>
          )}
          {redirecting && (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              sx={{ mt: 2 }}
            >
              10秒後にHome画面へ遷移します。
            </Typography>
          )}
        </>
      )}

      {/* ★ 紹介のみ表示（常に下部に） */}
      <FeaturesIntro />
    </Container>
  );
};

export default TokenVerificationPage;
